import { formatNumber } from '@angular/common';
import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { Price } from 'ish-core/models/price/price.model';
import { formatPrice } from 'ish-core/models/price/price.pipe';
import { formatISHDate } from 'ish-core/pipes/date.pipe';

import { Attribute } from './attribute.model';

@Pipe({
  name: 'ishAttribute',
  pure: false,
})
export class AttributeToStringPipe implements PipeTransform {
  private translateService = inject(TranslateService);

  private toDate(val: Date | number | string): string {
    return formatISHDate(val, 'shortDate', this.translateService.getCurrentLang());
  }

  private toDecimal(val: number): string {
    return formatNumber(val, this.translateService.getCurrentLang());
  }

  private toCurrency(price: Price): string {
    return formatPrice(price, this.translateService.getCurrentLang());
  }

  transform(data: Attribute, valuesSeparator: string = ', '): string {
    if (!this.translateService.getCurrentLang()) {
      return 'undefined';
    }

    switch (data.type) {
      case 'String':
        return data.value as string;
      case 'Integer':
      case 'Double':
      case 'Long':
      case 'BigDecimal':
        return this.toDecimal(data.value as number);
      case 'Boolean':
        return data.value.toString();
      case 'Date':
        return this.toDate(data.value as Date | number | string);
      case 'MultipleInteger':
      case 'MultipleDouble':
      case 'MultipleLong':
      case 'MultipleBigDecimal':
        return Array.isArray(data.value) && data.value.map(v => this.toDecimal(v)).join(valuesSeparator);
      case 'MultipleString':
      case 'MultipleBoolean':
        return Array.isArray(data.value) && data.value.join(valuesSeparator);
      case 'MultipleDate':
        return Array.isArray(data.value) && data.value.map(v => this.toDate(v)).join(valuesSeparator);
      case 'ResourceAttribute': {
        const resourceAttribute = data as Attribute<{ type: string }>;
        switch (resourceAttribute.value.type) {
          case 'Quantity': {
            const quantityAttribute = data as Attribute<{ value: unknown; unit: string }>;
            return `${this.toDecimal(quantityAttribute.value.value as number)}\xA0${quantityAttribute.value.unit}`;
          }
          case 'Money':
            return this.toCurrency(data.value as Price);
          default:
            return data.value.toString();
        }
      }
      default:
        return data.value.toString();
    }
  }
}
